// What the How to play window holds (CLAUDE.md: the How to play window).
//
// A tab per game, the daily's first, as in the Archive and Stats windows. Each
// tab is what the How it works page used to be: the rule line, then three steps,
// each a worked example with a caption — **smaller than the game draws them**,
// so the window reads as a window rather than as a page (owner, 12 October). The
// window scrolls where it is taller than the screen. There is no Play button at
// its foot: closing the window is the way back to whatever it was opened over.
//
// The examples use the game's real markup and stylesheet rather than a drawing
// of it — the same cards, the same CORRECT tab on the same frame. An example that
// is its own copy of the game is an example that goes stale the first time the
// game changes. Only their size is the window's own (HowToPlayPanel.css).
//
// The Running Order example is the design's, with Back to the Future in place of
// Aliens: the design pairs Alien with Aliens, which is the same TMDB collection
// and the free point the franchise rule exists to stop. It is this exact set of
// five that live generation refuses (`ro_example_set`), not the five films.
//
// The titles, years and image paths below are TMDB data copied from our own
// tables, never from the design file, whose artwork has since been replaced.
import { useState } from 'react';
import { ModalTabs } from './Modal.tsx';
import { Board } from '../games/running-order/Board.tsx';
import { posterUrl, releasedLabel, type AnswerFilm as RunningOrderFilm } from '../games/running-order/types.ts';
import { ActorTiles } from '../games/call-sheet/ActorTiles.tsx';
import type { Actor } from '../games/call-sheet/types.ts';
import type { HowToPlayGame } from '../lib/how-to-play.ts';
import { puzzleNameEm } from '../../shared/name-width.ts';
import '../games/running-order/Board.css';
import '../games/call-sheet/CallSheet.css';
import './HowToPlayPanel.css';

const GAMES: { id: HowToPlayGame; name: string }[] = [
  { id: 'running-order', name: 'Running Order' },
  { id: 'call-sheet', name: 'Call Sheet' }
];

export function HowToPlayPanel({ initial }: { initial: HowToPlayGame }) {
  const [tab, setTab] = useState<HowToPlayGame>(initial);
  return (
    <>
      <ModalTabs tabs={GAMES} selected={tab} onSelect={setTab} />
      <div className="htp">
        {tab === 'running-order' ? <HowRunningOrder /> : <HowCallSheet />}
      </div>
    </>
  );
}

// In release order, oldest first — the answer. Months from TMDB's release date,
// the date the game orders by, as the result screen shows them.
const FILMS: RunningOrderFilm[] = [
  { id: 348, title: 'Alien', year: 1979, month: 5, poster: '/vfrQk5IPloGg1v9Rzbh2Eg3VGyM.jpg' },
  { id: 78, title: 'Blade Runner', year: 1982, month: 6, poster: '/63N9uy8nd9j7Eog2axPQ8lbr3Wj.jpg' },
  { id: 105, title: 'Back to the Future', year: 1985, month: 7, poster: '/vN5B5WgYscRGcQpVhHl6p9DDTP0.jpg' },
  { id: 807, title: 'Se7en', year: 1995, month: 9, poster: '/191nKfP0ehp3uIvWqgPbFmI4lv9.jpg' },
  { id: 98, title: 'Gladiator', year: 2000, month: 5, poster: '/wN2xWp1eIwCKOD0BHTcErTBv1Uq.jpg' }
];

const by = (title: string) => FILMS.find((f) => f.title === title)!;
const ids = (...titles: string[]) => titles.map((t) => by(t).id);

// The shuffle the example starts from — a derangement, like every real puzzle's.
const START = ids('Se7en', 'Gladiator', 'Alien', 'Blade Runner', 'Back to the Future');
// After a guess that placed Alien and nothing else.
const AFTER = ids('Alien', 'Gladiator', 'Se7en', 'Blade Runner', 'Back to the Future');
const NONE = [false, false, false, false, false];

// The design's own Call Sheet example: The Shawshank Redemption, two actors at
// the start and a third after a wrong guess. **This one answer is kept out of
// the live pool** (`cs_example_answer_ids`) — an example that could turn up as a
// real puzzle is an answer given away. Only the answer is excluded, not the
// actors, who are free to appear in other puzzles.
const SHAWSHANK: Actor[] = [
  { id: 504, name: 'Tim Robbins', photo: '/3FfJMIVwXgsIXbAT8ECBSZJAncR.jpg' },
  { id: 192, name: 'Morgan Freeman', photo: '/905k0RFzH0Kd6gx8oSxRdnr6FL.jpg' },
  { id: 6573, name: 'William Sadler', photo: '/rWeb2kjYCA7V9MC9kRwRpm57YoY.jpg' },
  { id: 4029, name: 'Bob Gunton', photo: '/ulbVvuBToBN3aCGcV028hwO0MOP.jpg' }
];

function HowRunningOrder() {
  return (
    <>
      {/* How a game is served is said once, and this is the once. Short on
          purpose: the examples do the explaining (owner, 12 October). */}
      <p className="htp-rule">
        Put five films in release order, oldest first. Three guesses. New puzzle every day.
      </p>

      <div className="htp-steps">
        {/* Nothing is marked here, so nothing reserves room for a mark. */}
        <Step number={1} label="At the start" tabs={false} className="htp-ro"
              caption="Drag the posters into order. Same year? Earliest month first.">
          <Board films={FILMS} order={START} locked={NONE} onSwap={() => {}} disabled />
        </Step>

        <Step number={2} label="After a wrong guess" className="htp-ro"
              caption="Correct posters lock in place. Rearrange the rest.">
          <Board films={FILMS} order={AFTER} locked={[true, false, false, false, false]}
                 onSwap={() => {}} disabled />
        </Step>

        <Step number={3} label="Solved, or out of guesses" className="htp-ro"
              caption="See the release dates and how everyone did.">
          <Finished />
        </Step>
      </div>
    </>
  );
}

// The answer to the example, as the result screen leads with it: its poster,
// with the title and year under it. The path is the title table's own.
const SHAWSHANK_POSTER = '/9cqNxx0GxF0bflZmeSMuL5tnGzr.jpg';

// The example is spaced from its own four names, as every puzzle is.
const SHAWSHANK_EM = puzzleNameEm(SHAWSHANK.map((a) => a.name));

function HowCallSheet() {
  return (
    <div style={{ '--cs-name-em': SHAWSHANK_EM } as React.CSSProperties}>
      {/* How a game is served is said once, and this is the once. */}
      <p className="htp-rule">
        Name the movie or show from its cast. Three guesses. Play as many as you like.
      </p>

      <div className="htp-steps">
        <Step number={1} label="At the start"
              caption="Start with two actors. Type a title and pick it from the list.">
          <ActorTiles revealed={SHAWSHANK.slice(0, 2)} slots={4} />
        </Step>

        <Step number={2} label="After a wrong guess"
              caption="Each wrong guess or skip reveals another actor.">
          <ActorTiles revealed={SHAWSHANK.slice(0, 3)} slots={4} />
        </Step>

        <Step number={3} label="Solved, or out of guesses"
              caption="See the answer, full cast and how everyone did.">
          <figure className="htp-answer">
            <img className="htp-answer-poster" src={posterUrl(SHAWSHANK_POSTER)!} alt="" />
            <figcaption className="htp-answer-title">
              The Shawshank Redemption
              <span className="htp-year">1994</span>
            </figcaption>
          </figure>
        </Step>
      </div>
    </div>
  );
}

function Step({ number, label, caption, tabs = true, className = '', children }:
  { number: number; label: string; caption: string; tabs?: boolean; className?: string;
    children: React.ReactNode }) {
  return (
    <section className="htp-step">
      <h3 className="htp-step-label">{number} · {label}</h3>
      <div className={`htp-example${tabs ? '' : ' no-tabs'}${className ? ' ' + className : ''}`}>{children}</div>
      <p className="htp-caption">{caption}</p>
    </section>
  );
}

// The finished board: every card in place, marked, with the month and year it
// earned. The game's own board carries no years and its result strip carries no
// CORRECT tabs, so this one screen is assembled here — out of the same classes,
// so the tab and the frame are still the game's.
function Finished() {
  return (
    <div className="ro-board">
      {FILMS.map((film) => (
        <div className="ro-slot" key={film.id}>
          <div className="ro-card is-locked">
            <span className="ro-badgeslot"><span className="ro-correct-tab">Correct</span></span>
            {posterUrl(film.poster)
              ? <img className="ro-poster" src={posterUrl(film.poster)!} alt="" draggable={false} />
              : <span className="ro-poster ro-poster-missing" />}
            <span className="ro-title">
              {film.title}
              <span className="htp-year">{releasedLabel(film)}</span>
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}
